import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { Overlay } from 'src/components/Overlay';
import { useWorkspace } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import { IDomainCommandType } from '@publishing-engine/domain/actions/v1/commands';
import { IReviewer } from '@publishing-engine/domain/models';
import ComposeReviewerInvitation from './InviteReviewer/ComposeReviewerInvitation';
import { normalizeRecipients } from '../../../utils/normalizeEmailRecipients';
import { EmailContentForm } from '../../EmailModule';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';

type IProps = {
  onClose: () => void;
  submissionId: string;
  organizationId: string;
  submissionContextId: string;
  reviewId: string;
  reviewerId: string;
  reviewerEmail: string;
  reviewsQueryKey: string[];
  isCoReviewed: boolean;
  /**
   * The review's workflow state as read off the review, so it is typed as the
   * plain string it is rather than a four-state union the caller has to cast
   * into — casting is what let states with no command reach `onReinvite`.
   */
  status: string;
  submissionState: ISubmissionWFState;
  /**
   * Resolves `true` only when the command actually went through; a `false`
   * means the caller has already reported the failure, so the modal must stay
   * quiet rather than announce a reinvitation that never happened.
   */
  onReinvite: (
    reviewRequestId: string,
    reviewer: Pick<IReviewer, 'email' | 'id'>,
    currentState: string | undefined,
    emails?: IEmailCommandPayload
  ) => Promise<boolean>;
  previousState?: string;
};

export const ReinviteReviewerModal = ({
  onClose,
  organizationId,
  submissionContextId,
  reviewId,
  reviewerId,
  reviewerEmail,
  isCoReviewed,
  status,
  onReinvite,
  previousState
}: IProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();
  const { orgId: workspaceOrgId } = useWorkspace();

  const emails = useMemo(() => [reviewerEmail], [reviewerEmail]);

  const getCommandNameForStatus = (status: string): IDomainCommandType => {
    switch (status) {
      case 'declined':
        return 'ResendDeclinedReviewInvitation';
      case 'expired':
        return 'ResendExpiredReviewInvitation';
      case 'withdrawn':
        return 'ResendWithdrawnReviewInvitation';
      case 'awaitingRevisionReviewRequest':
        return 'RequestRevisionReview';
      default:
        return 'ResendDeclinedReviewInvitation';
    }
  };

  const commandName = getCommandNameForStatus(status);

  const { hasCustomizableTemplates, isLoading, templates } =
    useCommandEmailTemplates({
      organizationId: workspaceOrgId || organizationId,
      submissionContextId,
      commandName,
      ...(commandName === 'ResendWithdrawnReviewInvitation' && {
        query: {
          subtype:
            previousState === 'invited' || previousState === 'revisionInvited'
              ? 'invited'
              : 'assessment'
        }
      })
    });

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const emailAttachments = emailContentMethods.watch(
    'emailContent.attachments'
  );

  const isUploadingAttachments = useMemo(() => {
    return !!emailAttachments?.some(att => att.isUploading);
  }, [emailAttachments]);

  const handleReinviteWithEmail = async () => {
    setIsProcessing(true);
    try {
      const { getValues, trigger } = emailContentMethods;
      const emailContent = getValues('emailContent');

      const isValid = await trigger();
      if (!isValid) {
        scrollToFirstError();
        return;
      }

      const normalizedRecipients = normalizeRecipients(emailContent.recipients);

      const emails: IEmailCommandPayload = [
        {
          body: emailContent.body,
          subject: emailContent.subject,
          recipients: normalizedRecipients,
          attachments: emailContent.attachments
        }
      ];

      const didReinvite = await onReinvite(
        reviewId,
        { id: reviewerId, email: reviewerEmail },
        status,
        emails
      );

      if (!didReinvite) {
        return;
      }

      toast({
        variant: 'success',
        title: 'Reviewer Reinvited',
        description:
          'The reviewer will be notified about this decision via email.',
        isClosable: true
      });

      onClose();
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Reinvitation Failed',
        description: 'Failed to reinvite the reviewer. Please try again.',
        isClosable: true
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDirectReinvite = async () => {
    setIsProcessing(true);
    try {
      const didReinvite = await onReinvite(
        reviewId,
        { id: reviewerId, email: reviewerEmail },
        status
      );

      if (!didReinvite) {
        return;
      }

      toast({
        variant: 'success',
        title: 'Reviewer Reinvited',
        description:
          'The reviewer will be notified about this decision via email.',
        isClosable: true
      });

      onClose();
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Reinvitation Failed',
        description: 'Failed to reinvite the reviewer. Please try again.',
        isClosable: true
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const titleCopy = isCoReviewed ? 'Reinvite Reviewers?' : 'Reinvite Reviewer?';
  const bodyCopy = isCoReviewed
    ? 'The Reviewers will be notified by email, and their status will move to "Invited."'
    : 'This Reviewer will be notified by email, and their status will move to "Invited."';
  const buttonCta = isCoReviewed ? 'Reinvite Reviewers' : 'Reinvite Reviewer';

  // Render email browser if customizable templates exist
  if (hasCustomizableTemplates) {
    return (
      <Overlay>
        <FormProvider {...emailContentMethods}>
          <ComposeReviewerInvitation
            currentStep={0}
            totalSteps={0}
            emails={emails}
            isLoadingTemplates={isLoading}
            emailTemplates={templates}
            commandName={commandName}
            footer={
              <Flex width="100%" justifyContent="space-between">
                <Button variant="caution" onClick={onClose}>
                  Cancel
                </Button>

                <Flex gap={4}>
                  <Button
                    variant="primary"
                    // `disabled` is what actually blocks the click here: the
                    // design system forwards it past Chakra's own
                    // `isDisabled || isLoading`, so `isLoading` alone still
                    // leaves the button clickable and the action can re-fire.
                    disabled={isUploadingAttachments || isProcessing}
                    onClick={handleReinviteWithEmail}
                    isLoading={isProcessing}
                  >
                    {buttonCta}
                  </Button>
                </Flex>
              </Flex>
            }
          />
        </FormProvider>
      </Overlay>
    );
  }

  // Confirmation modal to directly reinvite if no customizable templates
  return (
    <Modal isOpen onClose={onClose} width="448px">
      <Flex padding={8} gap={4} flexDirection="column">
        <FontAwesomeIcon
          icon={faEnvelope}
          style={{ width: '40px', height: '40px' }}
        />
        <Text textStyle="headingGroup">{titleCopy}</Text>
        <Text>{bodyCopy}</Text>

        <Flex gap={2} justifyContent="flex-end">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleDirectReinvite}
            disabled={isProcessing}
            isLoading={isProcessing}
          >
            {buttonCta}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
